import React, { useState, useEffect } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Skeleton } from './ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { CalendarDays, Plus, Trash2, Save, Loader2, AlertTriangle } from 'lucide-react';
import { fehlendeFerienSchuljahre } from '../lib/utils';
import { toast } from 'sonner';

function HolidayRow({ entry, index, onChange, onRemove }) {
  return (
    <div className="flex items-end gap-2" data-testid={`holiday-row-${index}`}>
      <div className="flex-1">
        {index === 0 && <Label className="text-xs text-slate-500 mb-1 block">Bezeichnung</Label>}
        <Input
          value={entry.name}
          onChange={(e) => onChange(index, 'name', e.target.value)}
          placeholder="z.B. Sommerferien"
          className="h-9 text-sm"
          data-testid={`holiday-name-${index}`}
        />
      </div>
      <div className="w-36">
        {index === 0 && <Label className="text-xs text-slate-500 mb-1 block">Start</Label>}
        <Input
          type="date"
          value={entry.start}
          onChange={(e) => onChange(index, 'start', e.target.value)}
          className="h-9 text-sm"
          data-testid={`holiday-start-${index}`}
        />
      </div>
      <div className="w-36">
        {index === 0 && <Label className="text-xs text-slate-500 mb-1 block">Ende</Label>}
        <Input
          type="date"
          value={entry.end}
          onChange={(e) => onChange(index, 'end', e.target.value)}
          className="h-9 text-sm"
          data-testid={`holiday-end-${index}`}
        />
      </div>
      <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 hover:text-red-700 flex-shrink-0" onClick={() => onRemove(index)} data-testid={`holiday-remove-${index}`}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function SchoolHolidaysSettings() {
  const [allHolidays, setAllHolidays] = useState(null);
  const [selectedState, setSelectedState] = useState('');
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ auto_sync: null, laender_mit_zuordnungen: [] });

  useEffect(() => {
    settingsAPI.getSchoolHolidays().then(res => {
      setAllHolidays(res.data);
      const states = Object.keys(res.data).sort();
      if (states.length > 0) {
        setSelectedState(states[0]);
        setEntries(res.data[states[0]] || []);
      }
    }).catch(() => toast.error('Schulferien konnten nicht geladen werden'))
      .finally(() => setLoading(false));
    settingsAPI.getSchoolHolidaysStatus()
      .then(res => setStatus(res.data || { auto_sync: null, laender_mit_zuordnungen: [] }))
      .catch(() => {});
  }, []);

  const handleStateChange = (state) => {
    setSelectedState(state);
    setEntries(allHolidays[state] || []);
  };

  const handleEntryChange = (index, field, value) => {
    const updated = entries.map((e, i) => i === index ? { ...e, [field]: value } : e);
    setEntries(updated);
  };

  const addEntry = () => {
    setEntries([...entries, { name: '', start: '', end: '' }]);
  };

  const removeEntry = (index) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!selectedState) return;
    try {
      setSaving(true);
      const res = await settingsAPI.updateSchoolHolidays(selectedState, entries);
      setAllHolidays(prev => ({ ...prev, [selectedState]: entries }));

      const { resynced_timesheets = 0, conflicts = [] } = res.data || {};
      let msg = `Schulferien für ${selectedState} gespeichert`;
      if (resynced_timesheets > 0) {
        msg += ` — ${resynced_timesheets} offene Stundenzettel wurden angepasst`;
      }
      toast.success(msg);

      if (conflicts.length > 0) {
        toast.warning(
          `${conflicts.length} Stundenzettel enthalten Stunden an Tagen, die jetzt Ferien sind. ` +
          `Bitte manuell prüfen.`,
          { duration: 12000 }
        );
      }
    } catch (err) {
      toast.error(fehlertext(err, 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Card><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>;
  }

  const stateList = allHolidays ? Object.keys(allHolidays).sort() : [];
  const fehlendeJahre = selectedState ? fehlendeFerienSchuljahre(entries) : [];
  // Letztes hinterlegtes Ferienende und die Frage, ob das noch 12 Monate reicht.
  const letztesEnde = entries.reduce((max, e) => (e.end && e.end > max ? e.end : max), '');
  const inEinemJahr = new Date();
  inEinemJahr.setFullYear(inEinemJahr.getFullYear() + 1);
  const knappHinterlegt = !!selectedState
    && status.laender_mit_zuordnungen?.includes(selectedState)
    && (!letztesEnde || new Date(`${letztesEnde}T00:00:00`) < inEinemJahr);
  const autoSync = status.auto_sync;
  const autoDatum = autoSync?.zuletzt
    ? new Date(autoSync.zuletzt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;
  const rowElements = entries.map((entry, idx) => (
    <HolidayRow key={idx} entry={entry} index={idx} onChange={handleEntryChange} onRemove={removeEntry} />
  ));

  return (
    <Card data-testid="school-holidays-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <CalendarDays className="h-5 w-5" />
          Schulferien verwalten
        </CardTitle>
        <CardDescription>
          Schulferien werden von den Kultusministerien festgelegt und müssen hier gepflegt werden. Gesetzliche Feiertage werden automatisch berechnet und müssen nicht eingetragen werden.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500" data-testid="ferien-erklaertext">
          Neue Ferien werden wöchentlich automatisch ergänzt. Bewegliche Ferientage einzelner
          Schulen bitte selbst eintragen – sie werden nie überschrieben.
        </p>

        {(autoDatum || autoSync) && (
          <p className={`text-xs ${autoSync && autoSync.ok === false ? 'text-amber-700' : 'text-slate-500'}`}
             data-testid="ferien-auto-sync">
            {autoSync && autoSync.ok === false
              ? 'Automatischer Abgleich fehlgeschlagen – bitte Ferien prüfen'
              : `Automatischer Abgleich zuletzt am ${autoDatum}`}
          </p>
        )}
        {fehlendeJahre.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-1"
               data-testid="school-holidays-warning">
            {fehlendeJahre.map((jahr) => (
              <p key={jahr} className="flex items-start gap-2 text-xs text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>
                  Für das Schuljahr {jahr}/{jahr + 1} sind keine Schulferien hinterlegt.
                  Ferientage werden bis dahin als normale Arbeitstage gezählt.
                </span>
              </p>
            ))}
          </div>
        )}

        <div className="max-w-xs">
          <Label className="text-sm">Bundesland</Label>
          <Select value={selectedState} onValueChange={handleStateChange}>
            <SelectTrigger data-testid="school-holidays-state-select">
              <SelectValue placeholder="Bundesland wählen" />
            </SelectTrigger>
            <SelectContent>
              {stateList.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedState && (
          <>
            <p className="text-xs text-slate-500" data-testid="ferien-hinterlegt-bis">
              Ferien hinterlegt bis {letztesEnde
                ? new Date(`${letztesEnde}T00:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                : '—'}
            </p>

            {knappHinterlegt && (
              <p className="flex items-start gap-2 text-xs text-amber-800 rounded-lg border border-amber-300 bg-amber-50 p-3"
                 data-testid="ferien-reichweite-warnung">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>
                  Für {selectedState} sind weniger als 12 Monate in die Zukunft hinterlegt.
                  Bitte die kommenden Ferien ergänzen – hier eingesetzte Mitarbeiter haben Zuordnungen in diesem Bundesland.
                </span>
              </p>
            )}

            <div className="space-y-2" data-testid="school-holidays-list">
              {rowElements}
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={addEntry} data-testid="add-holiday-button">
                <Plus className="h-4 w-4 mr-1" />Zeitraum hinzufügen
              </Button>
              <Button className="bg-blue-800 hover:bg-blue-900" size="sm" onClick={handleSave} disabled={saving} data-testid="save-holidays-button">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Speichern
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
